import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { youtubeEmbedUrl, youtubePosterUrl } from "@/lib/youtubeEmbed";
import { listingImageUrl } from "@/lib/productGallery";
import { isMp4Url } from "@/lib/mediaKind";
import { ChevronLeft, ChevronRight, Play, Star, ShoppingBag } from "lucide-react";

function OrangeStars({ value = 0 }) {
  const n = Number(value) || 0;
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = n >= i ? 1 : n >= i - 0.5 ? 0.5 : 0;
        return (
          <span key={i} className="relative inline-block">
            <Star className="w-3.5 h-3.5 text-orange-200" />
            {fill > 0 ? (
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <Star className="w-3.5 h-3.5 text-orange-500" fill="#f97316" />
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

function discountLabel(p) {
  if (!p || !p.mrp || p.mrp <= p.price) return null;
  const pct = Math.round(((p.mrp - p.price) / p.mrp) * 100);
  if (pct <= 0) return null;
  return `${pct}% Off`;
}

function VideoPoster({ item, product, title }) {
  const [playing, setPlaying] = useState(false);
  const embed = youtubeEmbedUrl(item.video_url, { autoplay: true });
  const clip = item.clip_url || (isMp4Url(item.image_url) ? item.image_url : "");
  const poster = (!clip && (item.image_url || listingImageUrl(product?.images) || youtubePosterUrl(item.video_url))) || "";

  if (clip) {
    return (
      <div className="aspect-[9/16] w-full overflow-hidden rounded-[12px] bg-stone-900" data-testid="tiktok-clip">
        <video
          src={mediaUrl(clip)}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          disablePictureInPicture
        />
      </div>
    );
  }

  if (playing && embed) {
    return (
      <div className="aspect-[9/16] w-full overflow-hidden rounded-[12px] bg-stone-900">
        <iframe
          title={title}
          src={embed}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (embed) setPlaying(true);
      }}
      className="relative aspect-[9/16] w-full overflow-hidden rounded-[12px] bg-stone-900 group"
      data-testid="tiktok-play"
      aria-label="Play video"
    >
      {poster ? (
        <img src={mediaUrl(poster)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-stone-900" />
      )}
      <span className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
      {embed ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-12 h-12 rounded-full bg-[#ffffff] text-[#111111] flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform" data-testid="tiktok-play-icon">
            <Play className="w-5 h-5 ml-0.5 text-[#111111]" fill="#111111" stroke="#111111" />
          </span>
        </span>
      ) : null}
    </button>
  );
}

export default function TikTokCommerce({ homepage, primary, layout }) {
  const { slug, base, country } = useStoreBase();
  const hp = homepage || {};
  const block = hp.tiktok_commerce || {};
  const items = (block.items || []).filter((it) => it && (it.video_url || it.image_url || it.clip_url));
  const [products, setProducts] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    if (!hp.show_tiktok_commerce || items.length === 0) {
      setProducts([]);
      return;
    }
    const ids = items.map((it) => it.product_id).filter(Boolean);
    if (!ids.length) {
      setProducts([]);
      return;
    }
    const qs = new URLSearchParams({ ids: ids.join(",") });
    if (country) qs.set("country", country);
    api.get(`/store/${slug}/products?${qs}`)
      .then(({ data }) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
    // items is derived each render; stringify keeps the fetch stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, country, hp.show_tiktok_commerce, JSON.stringify(items)]);

  const cosmos = layout === "cosmos";
  useEffect(() => {
    if (!cosmos || items.length < 2 || !hp.show_tiktok_commerce) return undefined;
    const tick = () => {
      const el = scroller.current;
      if (!el) return;
      const card = el.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + 16 : 220;
      const half = el.scrollWidth / 2;
      const next = el.scrollLeft + step;
      if (next >= half - 4) el.scrollLeft = next - half;
      else el.scrollTo({ left: next, behavior: "smooth" });
    };
    const t = setInterval(tick, 2800);
    return () => clearInterval(t);
  }, [cosmos, items.length, hp.show_tiktok_commerce]);

  if (!hp.show_tiktok_commerce || items.length === 0) return null;

  const byId = Object.fromEntries(products.map((p) => [p.id, p]));
  const title = (layout === "perfecto" || layout === "boxy" || layout === "beast")
    ? ((block.title || "Watch and shop").trim() || "Watch and shop")
    : cosmos
      ? ((block.title || "Real People Real Results").trim() || "Real People Real Results")
    : ((block.title || "Watch & Shop With Us").trim() || "Watch & Shop With Us");
  const accent = primary || "#f97316";
  const perfecto = layout === "perfecto" || layout === "boxy" || layout === "beast";
  const beast = layout === "beast";
  const shown = cosmos && items.length > 1 ? [...items, ...items] : items;

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(280, el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <section
      className={cosmos
        ? "w-full max-w-none py-12 sm:py-16"
        : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16"}
      data-testid="tiktok-commerce"
    >
      <h2
        className={perfecto || cosmos
          ? `text-center font-display font-bold text-2xl sm:text-3xl ${beast ? "text-white" : "text-[#1c1917]"} normal-case tracking-normal px-4`
          : "text-center font-display font-extrabold uppercase tracking-[0.12em] text-xl sm:text-2xl"}
        style={perfecto || cosmos ? undefined : { color: accent }}
        data-testid="tiktok-commerce-title"
      >
        {title}
      </h2>
      <div className="relative mt-8">
        {items.length > 1 ? (
          <>
            <button
              type="button"
              aria-label="Previous videos"
              data-testid="tiktok-prev"
              onClick={() => scrollBy(-1)}
              className={`hidden md:flex absolute ${cosmos ? "left-3" : "-left-3"} top-[38%] z-10 w-10 h-10 items-center justify-center rounded-full ${beast ? "bg-black/70 text-white border border-white/20" : "bg-white border border-stone-200 shadow-md text-stone-500 hover:text-stone-900"}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              aria-label="Next videos"
              data-testid="tiktok-next"
              onClick={() => scrollBy(1)}
              className={`hidden md:flex absolute ${cosmos ? "right-3" : "-right-3"} top-[38%] z-10 w-10 h-10 items-center justify-center rounded-full ${beast ? "bg-black/70 text-white border border-white/20" : "bg-white border border-stone-200 shadow-md text-stone-500 hover:text-stone-900"}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        ) : null}
        <div
          ref={scroller}
          data-testid="tiktok-scroller"
          className={`flex gap-4 sm:gap-5 overflow-x-auto pb-3 no-scrollbar scroll-smooth snap-x snap-mandatory ${cosmos ? "px-3 sm:px-4" : "md:px-8"}`}
        >
          {shown.map((it, i) => {
            const p = byId[it.product_id];
            const off = discountLabel(p);
            const cls = cosmos
              ? "shrink-0 w-[46vw] max-w-[240px] sm:w-[210px] snap-start"
              : "shrink-0 w-[42vw] max-w-[220px] sm:w-[200px] snap-start";
            return (
              <div key={i} className={cls} data-testid={`tiktok-card-${i}`}>
                <div className={`relative ${cosmos ? "rounded-[18px] p-1 border" : ""}`} style={cosmos ? { borderColor: `${accent}55` } : undefined}>
                  <VideoPoster item={it} product={p} title={p?.name || `Watch and shop ${i + 1}`} />
                  {perfecto && p?.slug ? (
                    <Link
                      to={`${base}/product/${p.slug}`}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-[#ffffff] text-[#111111] flex items-center justify-center shadow"
                      aria-label="Shop this look"
                      data-testid="tiktok-shop-icon"
                    >
                      <ShoppingBag className="w-3.5 h-3.5 text-[#111111]" stroke="#111111" />
                    </Link>
                  ) : null}
                </div>
                {cosmos ? (
                  p?.slug ? (
                    <div className="mt-2.5 px-0.5">
                      <Link to={`${base}/product/${p.slug}`} className="block text-sm font-semibold text-stone-900 truncate whitespace-nowrap">{p.name}</Link>
                      <div className="mt-1 text-sm font-extrabold" style={{ color: accent }}>₹{Number(p.price || 0).toFixed(2)}</div>
                      <Link to={`${base}/product/${p.slug}`} className="mt-2 flex w-full h-9 items-center justify-center rounded-md text-xs font-bold text-white" style={{ background: accent }} data-testid={`tiktok-add-${i}`}>+ ADD TO CART</Link>
                    </div>
                  ) : null
                ) : perfecto ? null : p?.slug ? (
                  <Link to={`${base}/product/${p.slug}`} className="block mt-2.5">
                    <OrangeStars value={p?.rating_avg || 0} />
                    <div className="mt-1 text-sm font-medium text-stone-900 line-clamp-2 min-h-[2.5rem]">
                      {p?.name || "From Fashion To Makeup..."}
                    </div>
                    {off ? (
                      <span
                        className="mt-2 inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white"
                        style={{ background: accent }}
                      >
                        {off}
                      </span>
                    ) : null}
                  </Link>
                ) : (
                  <div className="mt-2.5">
                    <OrangeStars value={p?.rating_avg || 0} />
                    <div className="mt-1 text-sm font-medium text-stone-900 line-clamp-2 min-h-[2.5rem]">
                      {p?.name || "From Fashion To Makeup..."}
                    </div>
                    {off ? (
                      <span
                        className="mt-2 inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white"
                        style={{ background: accent }}
                      >
                        {off}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
